<template>
  <div>
    <div v-if="trainerLoading" style="text-align: center; padding: 10% ">
      <v-progress-circular :size="50" :width="4" color="primary" indeterminate></v-progress-circular>
    </div>
    <div v-else>
      <div class="trainer-detail">
        <v-card class="trainer-detail__card" flat>
          <v-card-text class="trainer-detail__body">
            <div class="mb-4">
              <div class="trainer-detail__label">Profile Verification</div>
              <v-chip :color="verificationColor" small outlined class="my-2">
                {{ verificationLabel }}
              </v-chip>
              <p class="mb-2">
                Verification confirms that a CPD reviewer has checked the trainer's profile and qualifications.
                Profile edits require a new review.
              </p>
              <p v-if="values.verifiedAt" class="mb-2">
                Reviewed: {{ new Date(values.verifiedAt).toLocaleString() }}
              </p>
              <p v-if="values.verificationComment" class="mb-2">
                Review comment: {{ values.verificationComment }}
              </p>
              <template v-if="values.canVerifyTrainer">
                <v-textarea
                    v-model="verificationComment"
                    label="Review comment (optional)"
                    rows="2"
                    outlined
                    dense
                    :disabled="verificationSaving"
                ></v-textarea>
                <v-btn
                    v-if="$can('trainer', 'verify') && (values.verificationStatus !== 'VERIFIED')"
                    color="primary"
                    class="mr-2"
                    :loading="verificationSaving && verificationAction === 'verify'"
                    :disabled="verificationSaving"
                    @click="reviewTrainer('verify')"
                >Verify Trainer</v-btn>
                <v-btn
                    v-if="$can('trainer', 'reject') && (values.verificationStatus !== 'REJECTED')"
                    color="error"
                    outlined
                    :loading="verificationSaving && verificationAction === 'reject'"
                    :disabled="verificationSaving"
                    @click="reviewTrainer('reject')"
                >Reject Verification</v-btn>
              </template>
            </div>
            <div class="trainer-detail__grid">
              <div class="trainer-detail__item">
                <div class="trainer-detail__label">Trainer Full Name</div>
                <div class="trainer-detail__value">
                  {{ values.fullName ? values.fullName : "-" }}
                </div>
              </div>
              <div class="trainer-detail__item">
                <div class="trainer-detail__label">Gender</div>
                <div class="trainer-detail__value">
                  {{ values.gender ? values.gender : "-" }}
                </div>
              </div>
              <div class="trainer-detail__item">
                <div class="trainer-detail__label">Educational Level</div>
                <div class="trainer-detail__value">
                  {{ values.educationalLevel ? values.educationalLevel.name : "-" }}
                </div>
              </div>
              <div class="trainer-detail__item">
                <div class="trainer-detail__label">Phone Number</div>
                <div class="trainer-detail__value">
                  {{ values.phoneNumber ? values.phoneNumber : "-" }}
                </div>
              </div>
              <div class="trainer-detail__item">
                <div class="trainer-detail__label">Email Address</div>
                <div class="trainer-detail__value">
                  {{ values.email ? values.email : "-" }}
                </div>
              </div>
              <div class="trainer-detail__item">
                <div class="trainer-detail__label">Trainer Selection Criteria</div>
                <div class="trainer-detail__value">
                  {{
                    values.trainerSelectionCriteria
                        ? values.trainerSelectionCriteria.name
                        : "-"
                  }}
                </div>
              </div>
              <div class="trainer-detail__item">
                <div class="trainer-detail__label">Profession</div>
                <div class="trainer-detail__value">
                  {{ values.profession ? values.profession.name : "-" }}
                </div>
              </div>
              <div class="trainer-detail__item">
                <div class="trainer-detail__label">Trainer Type</div>
                <div class="trainer-detail__value">
                  {{ values.trainerType ? values.trainerType.name : "-" }}
                </div>
              </div>
              <div class="trainer-detail__item">
                <div class="trainer-detail__label">Other Profession Name</div>
                <div class="trainer-detail__value">
                  {{ values.otherProfessionName ? values.otherProfessionName : "-" }}
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>

      <div class="trainer-contact-history mb-4">
        <v-card class="trainer-courses__card" flat>
          <v-tabs v-model="historyTab" centered fixed-tabs optional>
            <v-tab>contacts</v-tab>
            <v-tab>job history</v-tab>
          </v-tabs>

          <v-tabs-items v-model="historyTab">
            <v-tab-item>
              <div class="course-tab pa-4">
                <div class="flex">
                  <div class="w-1/3 h-12">
                    <p class="text-md-left font-weight-bold">Mobile Phone:</p>
                  </div>
                  <div class="w-2/3 h-12">
                    <div class=".text-base">
                      {{ primaryContact?.mobilephone || primaryContact?.phoneNumber || primaryContact?.phone || "-" }}
                    </div>
                  </div>
                </div>
                <div class="flex">
                  <div class="w-1/3 h-12">
                    <p class="text-md-left font-weight-bold">Email:</p>
                  </div>
                  <div class="w-2/3 h-12">
                    <div class=".text-base">
                      {{ primaryContact?.email || "-" }}
                    </div>
                  </div>
                </div>
              </div>
            </v-tab-item>
            <v-tab-item>
              <div class="course-tab pa-4">
                <div class="flex">
                  <div class="w-1/3 h-12">
                    <p class="text-md-left font-weight-bold">Job Title:</p>
                  </div>
                  <div class="w-2/3 h-12">
                    <div class=".text-base">
                      {{
                        primaryJobHistory?.jobtitlename || primaryJobHistory?.jobTitleName || primaryJobHistory?.jobTitle || "-"
                      }}
                    </div>
                  </div>
                </div>
                <div class="flex">
                  <div class="w-1/3 h-12">
                    <p class="text-md-left font-weight-bold">Joined Date:</p>
                  </div>
                  <div class="w-2/3 h-12">
                    <div class=".text-base">
                      {{ primaryJobHistory?.joineddate || primaryJobHistory?.joinedDate || "-" }}
                    </div>
                  </div>
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </div>

      <div class="trainer-detail__actions mb-4">
        <v-btn v-if="$can('trainer', 'assignCourse')" class="trainer-detail__primary" depressed @click="assignCourse()">
          Assign Course
        </v-btn>
      </div>

      <div class="trainer-courses">
        <v-card class="trainer-courses__card">
          <v-toolbar color="basil" elevation="6" flat>
            <v-text-field
                v-model="searchQuery"
                class="mx-2"
                flat
                hide-details
                label="Search for Course name"
                prepend-inner-icon="mdi-magnify"
                solo-inverted
                @input="updateFilteredCourses"
            ></v-text-field>
          </v-toolbar>

              <v-row>
                <v-col>
                  <v-btn v-if="currentPage > 1" @click="prevPage">Previous Page</v-btn>
                </v-col>
                <v-col class="text-center">
                  Page {{ currentPage }} of {{ totalPages }}
                </v-col>
                <v-col class="text-right">
                  <v-btn v-if="totalItems > itemsPerPage && currentPage < totalPages" @click="nextPage">Next Page
                  </v-btn>
                </v-col>
              </v-row>

              <div v-if="filteredCourses.length == 0">
                <v-card flat>
                  <v-card-title class="course-tab"> No courses to display!</v-card-title>
                </v-card>
              </div>

              <div v-if="filteredCourses.length > 0">
                <v-card flat>
                  <v-card-text v-for="course in filteredCourses" :key="course.id" class="course-tab">
                    <div class="flex">
                      <div class="w-1/3 h-12">
                        <p class="text-md-left font-weight-bold">Course Name:</p>
                      </div>
                      <div class="w-1/3 h-12">
                        <div class=".text-base">
                          {{
                            course.course ? course.course.courseName : (course.program ?
                                course.program.courseTitleCat2 : "-")
                          }}
                        </div>
                      </div>

                      <div>
                        <v-btn
                            v-if="$can('trainer', 'completeTraining') && (canCompleteCourse(course))"
                            class="teal white--text"
                            color="primary"
                            dark
                            depressed
                            small
                            :disabled="!isTrainingCompleted(course?.program?.endDate)"
                            @click="gotToCourseCompletion(course)"
                        >
                          <span>Complete Course</span>
                        </v-btn>
                        <v-btn
                            v-if="isCertificatePendingCoordinatorSignature(course) && !canSignCourse(course)"
                            class="teal white--text"
                            color="primary"
                            dark
                            depressed
                            small
                            disabled
                        >
                          <span>Pending Coordinator Signature</span>
                        </v-btn>
                        <v-btn
                            v-if="$can('trainer', 'sign') && (canSignCourse(course))"
                            class="white--text ml-2"
                            color="blue darken-3"
                            dark
                            depressed
                            small
                            @click="signCourseCertificate(course)"
                        >
                          <span>Sign Certificate</span>
                        </v-btn>
                        <v-btn
                            v-if="isCertificatePendingApproval(course) && !canApproveCourse(course)"
                            class="teal white--text"
                            color="primary"
                            dark
                            depressed
                            small
                            disabled
                        >
                          <span>Pending Approval</span>
                        </v-btn>
                        <v-btn
                            v-if="$can('trainer', 'approve') && (canApproveCourse(course))"
                            class="white--text ml-2"
                            color="indigo"
                            dark
                            depressed
                            small
                            @click="approveCourseCertificate(course)"
                        >
                          <span>Approve Certificate</span>
                        </v-btn>
                        <v-btn
                            v-if="$can('trainer', 'generateCertificate') && (canGenerateCertificate(course))"
                            class="teal white--text"
                            color="primary"
                            dark
                            depressed
                            small
                            :disabled="!isTrainingCompleted(course?.program?.endDate)"
                            @click="generateCerteficate(course)"
                        >
                          <span>View Certificate</span>
                        </v-btn>
                        <v-btn
                            v-if="$can('trainer', 'emailCertificate') && (canGenerateCertificate(course))"
                            class="white--text ml-2"
                            color="green"
                            dark
                            depressed
                            small
                            :disabled="!isTrainingCompleted(course?.program?.endDate)"
                            @click="sendCertificateEmail(course)"
                        >
                          <span>Send Certificate</span>
                        </v-btn>
                        <v-btn
                            v-if="$can('trainer', 'editAssignment') && (isCertificateGenerated(course) && canManageActivity(course))"
                            class="white--text ml-2"
                            color="cyan"
                            dark
                            depressed
                            small
                            :disabled="course.certificateGenerated === true"
                            @click="editCourseDetail(course)"
                        >
                          <span>Edit</span>
                        </v-btn>
                      </div>
                      <div
                          v-if="!isTrainingCompleted(course?.program?.endDate)"
                          class="text-caption red--text mt-2"
                      >
                        Training event has not been completed yet. Complete Course and Certificate actions are
                        disabled until {{ course?.program?.endDate || "the end date" }}.
                      </div>
                    </div>
                    <div class="flex">
                      <div class="w-1/3 h-12">
                        <p class="text-md-left font-weight-bold">
                          Continuing Education Unit:
                        </p>
                      </div>
                      <div class="w-1/3 h-12">
                        <div class=".text-base">
                          {{
                            course.course ? course.course.continualEducationUnit : (course.program ?
                                course.program.ceu : "-")
                          }}
                        </div>
                      </div>
                    </div>
                    <div class="flex">
                      <div class="w-1/3 h-12">
                        <p class="text-md-left font-weight-bold">
                          CPD Activity:
                        </p>
                      </div>
                      <div class="w-1/3 h-12">
                        <div class=".text-base">
                          {{ course.program ? course.program.category ? course.program.category.name : "-" : "-" }}
                        </div>
                      </div>
                    </div>
                    <div class="flex">
                      <div class="w-1/3 h-12">
                        <p class="text-md-left font-weight-bold">
                          Period:
                        </p>
                      </div>
                      <div class="w-1/3 h-12">
                        <div class=".text-base"
                             v-html="course.program ? course.program.startDate ? `<b>From:</b> ${course.program.startDate} <br><b>To:</b> ${course.program.endDate}` : '-' : '-'">
                        </div>
                      </div>
                    </div>
                    <div class="flex">
                      <div class="w-1/3 h-12">
                        <p class="text-md-left font-weight-bold">
                          Certificate Reason:
                        </p>
                      </div>
                      <div class="w-1/3 h-12">
                        <div class=".text-base">
                          {{ course?.certificate_reason ? course.certificate_reason.name : 'Not Certified Yet' }}
                        </div>
                      </div>
                    </div>
                    <div class="flex">
                      <div class="w-1/3 h-12">
                        <p class="text-md-left font-weight-bold">
                          Course Completion Date:
                        </p>
                      </div>
                      <div class="w-1/3 h-12">
                        <div class=".text-base">
                          {{ course.courseCompletionDate ? course.courseCompletionDate : 'Not Certified Yet' }}
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </div>

        </v-card>
      </div>

      <template>
        <v-dialog v-model="courseCompletionDialog" width="60%">
          <v-card>
            <v-card-title class="headline font-weight-regular blue-grey white--text" primary-title>{{
                title
              }}
            </v-card-title>
            <v-divider></v-divider>
            <TrainerCourseCompletion v-if="courseCompletionDialog"
                                     :trainerCourseDetail="trainerCourseDetail"
                                     :trainerEmail="values?.email"
                                     @close="closeDialog()" @completeCourse="completeCourse"></TrainerCourseCompletion>
          </v-card>
        </v-dialog>
      </template>
      <template>
        <v-dialog v-model="assignCourseDialog" persistent width="60%">
          <v-card>
            <v-card-title class="headline font-weight-regular blue-grey white--text" primary-title>{{ assignTitle }}

              <v-spacer></v-spacer>

              <v-btn color="white" text @click="closeDialog()">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <AssignTrainerCourse v-if="assignCourseDialog" @close="closeDialog()"
                                 @saveAssignedCourse="saveAssignedCourse"></AssignTrainerCourse>
          </v-card>
        </v-dialog>
      </template>
      <template>
        <v-dialog v-model="editCourseDialog" width="60%">
          <v-card>
            <v-card-title
                class="headline font-weight-regular blue-grey white--text"
                primary-title
            >Edit Trainer's Certificate
            </v-card-title
            >
            <v-divider></v-divider>
            <AssignTrainerCourse
                v-if="editCourseDialog"
                :trainer="trainerCourseDetail"
                @close="closeDialog()"
                @saveAssignedCourse="saveAssignedCourse"
            ></AssignTrainerCourse>
          </v-card>
        </v-dialog>
      </template>
    </div>
  </div>
</template>

<script>
import TrainerCourseCompletion from "./trainer-course-completion.vue";
import {generateTCertificate} from "./gerate-trainer-certificate";
import {TrainerService} from "./trainers.service";
import AssignTrainerCourse from "./assign-trainer-course.vue";
import {settings} from "@/Settings";
import {
  hasCertificateApprovalPermission,
  hasCertificateSigningPermission,
  isCertificateApprovalComplete,
  isCertificateApprovalPending,
  isCertificatePendingCoordinatorSignature,
} from "@/utils/helper";

export default {
  props: {
    trainer: Object,
  },
  components: {
    TrainerCourseCompletion,
    AssignTrainerCourse,
  },
  data() {
    return {
      currentPage: 1,
      itemsPerPage: 5,
      values: null,
      courseCompletionDialog: false,
      trainerCourseDetail: null,
      title: null,
      imageSrc: "",
      assignCourseDialog: false,
      assignTitle: null,
      trainerLoading: false,
      verificationComment: "",
      verificationSaving: false,
      verificationAction: null,
      historyTab: null,
      searchQuery: '',
      editCourseDialog: false
    };
  },
  created() {
    if (this.trainer != null) {
      this.values = this.trainer;
    }
  },
  computed: {
    verificationLabel() {
      return {
        VERIFIED: "Verified",
        REJECTED: "Rejected",
      }[this.values?.verificationStatus] || "Pending verification";
    },
    verificationColor() {
      return {
        VERIFIED: "success",
        REJECTED: "error",
      }[this.values?.verificationStatus] || "warning";
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    totalItems() {
      const defaultTotalItems = (
          (this.values?.trainer_courses ? this.values.trainer_courses.length : 0) +
          (this.values?.trainer_cat2_courses ? this.values.trainer_cat2_courses.length : 0)
      );
      return defaultTotalItems == 0 ? 1 : defaultTotalItems
    },
    trainerContacts() {
      if (this.values?.person?.contacts && this.values.person.contacts.length > 0) {
        return this.values.person.contacts;
      }
      if (this.values?.contacts && this.values.contacts.length > 0) {
        return this.values.contacts;
      }
      if (this.values?.phoneNumber || this.values?.email) {
        return [{
          mobilephone: this.values.phoneNumber || null,
          email: this.values.email || null,
        }];
      }
      return [];
    },
    primaryContact() {
      return this.trainerContacts.length > 0 ? this.trainerContacts[0] : null;
    },
    trainerJobHistories() {
      if (this.values?.person?.employee_job_histories && this.values.person.employee_job_histories.length > 0) {
        return this.values.person.employee_job_histories;
      }
      if (this.values?.employee_job_histories && this.values.employee_job_histories.length > 0) {
        return this.values.employee_job_histories;
      }
      return [];
    },
    primaryJobHistory() {
      return this.trainerJobHistories.length > 0 ? this.trainerJobHistories[0] : null;
    },
    canApproveCertificate() {
      return this.$can("trainer","approve",hasCertificateApprovalPermission());
    },
    canSignCertificate() {
      return this.$can("trainer","sign",hasCertificateSigningPermission());
    },
    filteredCourses() {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      const normalizedQuery = this.searchQuery ? this.searchQuery.toLowerCase() : "";

      const allCourses = [
        ...(this.values?.trainer_courses || []),
        ...(this.values?.trainer_cat2_courses || [])
      ];

      const filteredCourses = allCourses.filter(course => {
        const categoryOneCourseName = course?.course?.courseName
            ? course.course.courseName.toLowerCase()
            : "";
        const categoryTwoCourseName = course?.program?.courseTitleCat2
            ? course.program.courseTitleCat2.toLowerCase()
            : "";
        if (!normalizedQuery) {
          return true;
        }
        return (
            categoryOneCourseName.includes(normalizedQuery) ||
            categoryTwoCourseName.includes(normalizedQuery)
        );
      });

      const mappedCourses = [
        ...((filteredCourses && filteredCourses.length > 0) ? filteredCourses.slice(startIndex, endIndex) : []),
      ];


      return mappedCourses
    }
  },
  methods: {
    async reviewTrainer(action) {
      if (!this.values?.canVerifyTrainer || this.verificationSaving) return;
      this.verificationSaving = true;
      this.verificationAction = action;
      try {
        const res = await TrainerService.verifyTrainer(
            this.values.id, {action, comment: this.verificationComment.trim()}
        );
        if (res.data?.status !== 200) {
          this.$toast.error(res.data?.message || "Unable to review trainer.");
          return;
        }
        this.values = {...this.values, ...res.data.data};
        this.verificationComment = "";
        this.$toast.success(res.data.message);
      } catch (error) {
        this.$toast.error(error.response?.data?.message || "Unable to review trainer.");
      } finally {
        this.verificationSaving = false;
        this.verificationAction = null;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage += 1;
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage -= 1;
      }
    },
    updateFilteredCourses() {
      this.currentPage = 1;
    },
    isCategory2Course(course) {
      if (!course) return false;
      return !course?.course && !!course?.program;
    },
    isCertificatePendingApproval(course) {
      if (!course) return false;
      return isCertificateApprovalPending(course);
    },
    isCertificatePendingCoordinatorSignature(course) {
      if (!course) return false;
      return isCertificatePendingCoordinatorSignature(course);
    },
    isCertificateGenerated(course) {
      if (!course) return false;
      return course?.certificateGenerated === true && isCertificateApprovalComplete(course);
    },
    canManageActivity(course) {
      return course?.canManageActivity !== false;
    },
    canGenerateCertificate(course) {
      return this.canManageActivity(course) && this.isCertificateGenerated(course);
    },
    canCompleteCourse(course) {
      if (!course) return false;
      return this.canManageActivity(course) &&
          course?.certificateGenerated !== true &&
          !this.isCertificatePendingApproval(course) &&
          !this.isCertificatePendingCoordinatorSignature(course);
    },
    canApproveCourse(course) {
      if (!course) return false;
      return this.canApproveCertificate &&
          this.canManageActivity(course) &&
          this.isCertificatePendingApproval(course);
    },
    canSignCourse(course) {
      if (!course) return false;
      return this.canSignCertificate &&
          this.canManageActivity(course) &&
          this.isCertificatePendingCoordinatorSignature(course);
    },
    gotToCourseCompletion(trainerCourse) {
      if (!this.canManageActivity(trainerCourse)) {
        this.$toast.error("You can only complete activities owned by your CPD provider.");
        return;
      }
      this.courseCompletionDialog = true;
      this.trainerCourseDetail = trainerCourse;
      this.title = "Course Completion Detail";
    },
    async fetchTrainer() {
      this.trainerLoading = true;
      const res = await TrainerService.getTrainer(this.trainer.id);
      if (res.status === 200) {
        this.trainerLoading = false;
        this.values = res.data.data;
        this.values.fullName = this.values.trainerName + " " + this.values.trainerFatherName + " " + this.values.trainerGrandfatherName
      } else {
        this.trainerLoading = false;
        this.$toast.error(
            "There was a problem fetching trainers, Please try Again!"
        );
      }

    },
    editCourseDetail(courseDetail) {
      if (!this.canManageActivity(courseDetail)) {
        this.$toast.error("You can only edit activities owned by your CPD provider.");
        return;
      }
      this.editCourseDialog = true;
      this.trainerCourseDetail = courseDetail;
      this.title = "Edit Trainer's Course Detail";
    },
    closeDialog() {
      this.assignCourseDialog = false;
      this.courseCompletionDialog = false;
      this.editCourseDialog = false;
      this.fetchTrainer();
    },
    assignCourse() {
      this.assignCourseDialog = true;
      this.assignTitle = 'Assign Trainer Course'
    },
    async saveAssignedCourse(param) {
      const res = await TrainerService.update(this.trainer.id, param);

      if (res.status === 201 || res.status === 200) {
        switch (res.data.status) {
          case 200:
            this.$toast.success(res.data.message);
            this.closeDialog();
            break;
          case 500:
            this.$toast.error(res.data.message);
            break;
        }
      } else {
        this.$toast.error("Unable to update trainer, please try again");
      }

    },
    async completeCourse(param) {
      if (!this.isTrainingCompleted(param?.program?.endDate)) {
        this.$toast.error("Training event has not been completed yet.");
        return;
      }
      const res = await TrainerService.completeTrainerCourse(param);
      if (!res) {
        console.error('No response from TrainerService.completeTrainerCourse');
        return;
      }

      if (res.status === 200 || res.status === 201) {
        if (res.data.status === 200 || res.data.status === 201) {
          this.$toast.success("Course completion submitted for certificate approval.");
          this.closeDialog();
        } else if (res.data.status === 500) {
          this.$toast.error(res.data.message);
        }
      } else {
        this.$toast.error("Unable to complete trainer course, please try again");
      }
    },
    async generateCerteficate(trainerUpdateCourse, options = {}) {
      const {open = true, sendEmail = false} = options || {};
      if (!this.canManageActivity(trainerUpdateCourse)) {
        this.$toast.error("You can only generate certificates for activities owned by your CPD provider.");
        return;
      }
      if (this.isCertificatePendingApproval(trainerUpdateCourse)) {
        this.$toast.warning("Certificate is pending approval.");
        return;
      }
      if (sendEmail && (!this.values?.email || this.values.email.trim() === "")) {
        this.$toast.error("Trainer email is required to deliver the certificate.");
        return;
      }
      if (!this.isTrainingCompleted(trainerUpdateCourse?.program?.endDate)) {
        this.$toast.error("Training event has not been completed yet.");
        return;
      }
      trainerUpdateCourse ? trainerUpdateCourse.trainer = this.values : null;
      const category2 = this.isCategory2Course(trainerUpdateCourse) ? 2 : 1;
      const url = settings.FRONT_URL + "trainerInformation/" + this.trainer.id + "/" + category2 + "/" + trainerUpdateCourse.id;
      const params = {
        url: url
      }

      const res = await TrainerService.generateQrCode(params);

      if (res.status === 200 || res.status === 201) {
        this.imageSrc = res.data;
        switch (res.data.status) {
          case 200 || 201:
            generateTCertificate(trainerUpdateCourse, this.imageSrc.data, {open, sendEmail});
            break;
          case 500:
            this.$toast.error(res.data.message);
            break;
        }
      } else {
        this.$toast.error("Unable to generate certificate, please try again");
      }
    },
    sendCertificateEmail(trainerUpdateCourse) {
      return this.generateCerteficate(trainerUpdateCourse, {open: false, sendEmail: true});
    },
    async approveCourseCertificate(course) {
      if (!this.canApproveCertificate) {
        this.$toast.error("You do not have permission to approve certificates.");
        return;
      }
      if (!this.canManageActivity(course)) {
        this.$toast.error("You can only approve certificates for activities owned by your CPD provider.");
        return;
      }
      if (!this.isCertificatePendingApproval(course)) {
        this.$toast.error("Only pending certificates can be approved.");
        return;
      }
      const trainer = [{
        id: course.id,
        category: this.isCategory2Course(course) ? "cat2" : "cat1",
      }];
      const res = await TrainerService.approveCertificates("trainer", {trainer});
      if (res.status === 200 || res.status === 201) {
        if (res.data.status === 200) {
          this.$toast.success("Certificate approved successfully.");
          await this.fetchTrainer();
        } else {
          this.$toast.error(res.data.message || "Unable to approve certificate.");
        }
      } else {
        this.$toast.error("Unable to approve certificate.");
      }
    },
    async signCourseCertificate(course) {
      if (!this.canSignCourse(course)) {
        this.$toast.error("Only certificates awaiting the CPD Coordinator signature can be signed.");
        return;
      }
      const trainer = [{
        id: course.id,
        category: this.isCategory2Course(course) ? "cat2" : "cat1",
      }];
      const res = await TrainerService.signCertificates("trainer", {trainer});
      if ((res.status === 200 || res.status === 201) && res.data.status === 200) {
        this.$toast.success(res.data.message || "Certificate signed successfully.");
        await this.fetchTrainer();
        return;
      }
      this.$toast.error(res.data?.message || "Unable to sign certificate.");
    },
    isTrainingCompleted(endDate) {
      if (!endDate) return true;
      const programEndDate = new Date(endDate);
      const today = new Date();
      programEndDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      if (Number.isNaN(programEndDate.getTime())) return true;
      return programEndDate <= today;
    },
  }
};
</script>

<style>
.trainer-detail__body {
  padding: 16px 20px 24px;
}

.trainer-detail__grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 12px;
}

@media (min-width: 768px) {
  .trainer-detail__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.trainer-detail__item {
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  min-height: 64px;
}

.trainer-detail__label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 6px;
  display: block;
}

.trainer-detail__value {
  font-size: 13px;
  color: #0f172a;
}

.trainer-detail__actions {
  display: flex;
  justify-content: flex-end;
  padding: 8px 16px 16px;
}

.trainer-detail__primary {
  background: #1976d2 !important;
  border-color: #1976d2 !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
}

.course-tab {
  background: whitesmoke;
  border: 1px solid lightblue;
}
</style>
